/**
 * DraggablePlayerCard — wraps an active (on-floor) PlayerCard so it acts
 * as both a drag SOURCE (floor → bench) and a drag TARGET (bench → floor).
 *
 * The PlayerCard is rendered inside a Box that holds the dnd refs. Action
 * buttons inside the card stay clickable — react-dnd's HTML5 backend
 * doesn't intercept clicks on nested interactive elements.
 *
 * Drop callback: `onSwap(droppedBenchPlayer, thisFloorPlayer)`.
 */

import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box } from '@mui/material';
import PlayerCard from '../PlayerCard';
import { useStatsTheme, withAlpha } from '../../theme/statsTheme';
import { ITEM_TYPE_PLAYER, PLAYER_SOURCES } from './DndStatsProvider';

export default function DraggablePlayerCard({
  player,
  timeOnFloorSeconds,
  fouls,
  onSubstitute,
  onMore,
  onSwap,
  children,
}) {
  const theme = useStatsTheme();

  // Floor card is a drag source (floor → bench)
  const [{ isDragging }, dragRef, dragPreview] = useDrag(() => ({
    type: ITEM_TYPE_PLAYER,
    item: { player, source: PLAYER_SOURCES.FLOOR },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }), [player]);

  // Floor card is also a drop target — accepts a bench player to swap with
  const [{ isOver, canDrop }, dropRef] = useDrop(() => ({
    accept: ITEM_TYPE_PLAYER,
    canDrop: (dragItem) => dragItem.source === PLAYER_SOURCES.BENCH,
    drop: (dragItem) => {
      if (typeof onSwap === 'function') onSwap(dragItem.player, player);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  }), [player, onSwap]);

  // Hide the native preview — StatsCustomDragLayer paints its own
  React.useEffect(() => {
    if (typeof dragPreview === 'function') {
      const empty = new Image();
      empty.src = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
      dragPreview(empty);
    }
  }, [dragPreview]);

  const isHighlighted = isOver && canDrop;

  return (
    <Box
      ref={(node) => {
        dragRef(node);
        dropRef(node);
      }}
      sx={{
        position: 'relative',
        opacity: isDragging ? 0.4 : 1,
        cursor: 'grab',
        transition: `transform ${theme.motion.fast}, box-shadow ${theme.motion.fast}`,
        transform: isHighlighted ? 'scale(1.02)' : 'none',
        ...(isHighlighted && {
          boxShadow: `0 0 0 3px ${withAlpha(theme.accent, 0.55)}, 0 16px 36px ${withAlpha(theme.accent, 0.32)}`,
          borderRadius: `${theme.radius.lg}px`,
        }),
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <PlayerCard
        player={player}
        timeOnFloorSeconds={timeOnFloorSeconds}
        fouls={fouls}
        onSubstitute={onSubstitute}
        onMore={onMore}
        highlight={isHighlighted}
      >
        {children}
      </PlayerCard>
    </Box>
  );
}
